import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageContent from '../hooks/usePageContent';
import GallerySection from '../components/GallerySection';
import { businessInfo } from '../data';

export default function Home() {
  const { page, loading } = usePageContent('forside');

  useEffect(() => {
    if (page) {
      document.title = page.title;
      // Update meta description dynamically
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.meta_description);
      }
    }
  }, [page]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-beige">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-sage border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-brand-beige">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            
            {/* Left Content */}
            <div className="flex flex-col gap-6 lg:col-span-7">
              <span className="inline-flex items-center gap-2 self-start rounded-full bg-brand-sage-light px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-brand-sage-dark">
                <i className="fa-solid fa-circle-check text-[10px]" /> RAB-Registreret Behandler i Broby
              </span>
              <h1 className="font-serif text-4xl font-semibold leading-tight text-brand-neutral-dark md:text-5xl lg:text-6xl">
                {page.hero_title}
              </h1>
              <p className="text-base leading-relaxed text-brand-neutral-dark/80 md:text-lg">
                {page.hero_subtitle}
              </p>
              
              {/* CTAs */}
              <div className="mt-4 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center rounded-full bg-brand-sage px-8 py-3.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark hover:shadow-md focus:outline-none focus:ring-2 focus:ring-brand-sage focus:ring-offset-2"
                >
                  Book tid <i className="fa-light fa-calendar-days ml-2" />
                </Link>
                <Link
                  to="/bowen-terapi"
                  className="inline-flex items-center justify-center rounded-full border border-brand-sage-medium/40 bg-white/40 px-8 py-3.5 text-sm font-medium text-brand-neutral-dark transition-all duration-300 hover:bg-white/80 hover:border-brand-sage focus:outline-none"
                >
                  Læs om Bowen Terapi
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative lg:col-span-5">
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-xl md:aspect-[16/10] lg:aspect-square">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/d7ec2ecb-d3f6-4a99-ad2f-51a97712ea7d/655110563-26491603377100590-2152733476049743188-n.jpg"
                  alt="Lyst og roligt behandlingsrum hos Stausbroe Bowen Terapi"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-neutral-dark/10 to-transparent" />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 -z-10 h-24 w-24 rounded-full bg-brand-sage-medium/20 blur-xl" />
              <div className="absolute -top-6 -right-6 -z-10 h-32 w-32 rounded-full bg-brand-cream/60 blur-xl" />
            </div>

          </div>
        </div>
      </section>

      {/* Trust / Core Values Section */}
      <section className="bg-brand-cream/30 py-16 md:py-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
              Vores Værdier
            </span>
            <h2 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl">
              Nænsom behandling i trygge rammer
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-brand-neutral-dark/80 md:text-base">
              Hos Stausbroe Bowen Terapi er du i centrum. Behandlingen tilpasses altid din krops behov og grænser, så du kan finde ro og balance.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {/* Value 1 */}
            <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-6 shadow-sm transition-all duration-300">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                <i className="fa-light fa-spa text-xl" />
              </div>
              <h3 className="mt-4 font-serif text-lg font-semibold text-brand-neutral-dark">Dyb ro</h3>
              <p className="mt-2 text-xs leading-relaxed text-brand-neutral-dark/80">
                Behandlingen foregår i en atmosfære af fuldstændig ro, hvor du kan koble helt af fra hverdagens stress.
              </p>
            </div>

            {/* Value 2 */}
            <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-6 shadow-sm transition-all duration-300">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                <i className="fa-light fa-hand-heart text-xl" />
              </div>
              <h3 className="mt-4 font-serif text-lg font-semibold text-brand-neutral-dark">Nærvær</h3>
              <p className="mt-2 text-xs leading-relaxed text-brand-neutral-dark/80">
                Jeg tager mig altid god tid til dig, lytter til din krop og sikrer fuldt fokus under hele forløbet.
              </p>
            </div>

            {/* Value 3 */}
            <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-6 shadow-sm transition-all duration-300">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                <i className="fa-light fa-feather text-xl" />
              </div>
              <h3 className="mt-4 font-serif text-lg font-semibold text-brand-neutral-dark">Blid behandling</h3>
              <p className="mt-2 text-xs leading-relaxed text-brand-neutral-dark/80">
                Ingen hårde tryk eller knæk. Kun blide, præcise greb, der respekterer din krops naturlige grænser.
              </p>
            </div>

            {/* Value 4 */}
            <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-6 shadow-sm transition-all duration-300">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                <i className="fa-light fa-shield-heart text-xl" />
              </div>
              <h3 className="mt-4 font-serif text-lg font-semibold text-brand-neutral-dark">Trygge rammer</h3>
              <p className="mt-2 text-xs leading-relaxed text-brand-neutral-dark/80">
                Klinikken er indrettet lyst og indbyandende, så du føler dig tryg og velkommen fra første sekund.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bowen Terapi Forklaret Helt Enkelt */}
      <section className="bg-brand-sage-light/30 py-16 md:py-24 border-y border-brand-sage-medium/10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-3xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
              Så nemt er det
            </span>
            <h2 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl">
              Bowen Terapi forklaret helt enkelt
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-brand-neutral-dark/80 md:text-base">
              Mange kender ikke behandlingen på forhånd, men princippet er utrolig enkelt, naturligt og behageligt. Det foregår i tre simple trin:
            </p>
          </div>

          <div className="mt-16 grid grid-cols-1 gap-12 lg:grid-cols-3">
            {/* Step 1 */}
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-3xl shadow-sm border border-brand-sage-medium/10">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage-dark mb-4">
                <i className="fa-light fa-hand-holding-heart text-2xl" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-brand-neutral-dark">De blide greb</h3>
              <p className="mt-3 text-xs leading-relaxed text-brand-neutral-dark/80">
                Behandlingen består af små, præcise og rullende bevægelser med fingrene over specifikke punkter på muskler, sener og bindevæv. Det gør ikke ondt og føles yderst behageligt.
              </p>
            </div>

            {/* Step 2 */}
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-3xl shadow-sm border border-brand-sage-medium/10">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage-dark mb-4">
                <i className="fa-light fa-hourglass-clock text-2xl" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-brand-neutral-dark">De vigtige pauser</h3>
              <p className="mt-3 text-xs leading-relaxed text-brand-neutral-dark/80">
                Mellem grebene holder vi små pauser på et par minutter, hvor jeg træder væk fra kroppen. Det giver dit nervesystem fuldstændig ro til at modtage og integrere impulserne.
              </p>
            </div>

            {/* Step 3 */}
            <div className="flex flex-col items-center text-center p-6 bg-white rounded-3xl shadow-sm border border-brand-sage-medium/10">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage-dark mb-4">
                <i className="fa-light fa-sparkles text-2xl" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-brand-neutral-dark">Kroppens egen heling</h3>
              <p className="mt-3 text-xs leading-relaxed text-brand-neutral-dark/80">
                Nervesystemet stimuleres til at slippe dybe spændinger, forbedre blodcirkulationen og genstarte kroppens naturlige selvhelbredende processer for dyb og varig lindring.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
            
            {/* Left Image */}
            <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-lg md:aspect-[16/10] lg:aspect-square">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/d7ec2ecb-d3f6-4a99-ad2f-51a97712ea7d/480431109-1171764351405014-4491373036087746201-n.jpg"
                alt="Nænsom berøring under en Bowen-behandling"
                className="h-full w-full object-cover"
              />
            </div>

            {/* Right Content */}
            <div className="flex flex-col gap-6">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
                Hvad er Bowen Terapi?
              </span>
              <h2 className="font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl">
                En skånsom vej til velvære
              </h2>
              <div 
                className="prose prose-stone text-sm leading-relaxed text-brand-neutral-dark/80"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />
              <div className="mt-2">
                <Link
                  to="/bowen-terapi"
                  className="inline-flex items-center text-sm font-semibold text-brand-sage-dark hover:text-brand-sage transition-colors duration-300"
                >
                  Læs mere om behandlingsformen <i className="fa-light fa-arrow-right ml-2" />
                </Link>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Diskret RAB-markering */}
      <section className="bg-brand-sage-light/40 py-12">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-brand-sage shadow-sm mb-4">
            <i className="fa-light fa-award text-xl" />
          </div>
          <h3 className="font-serif text-xl font-semibold text-brand-neutral-dark">RAB-registreret kropsterapeut</h3>
          <p className="mt-2 text-sm text-brand-neutral-dark/80 max-w-2xl mx-auto">
            Min RAB-registrering (Registreret Alternativ Behandler) er din sikkerhed for en professionel uddannelse, etiske retningslinjer og løbende efteruddannelse.
          </p>
          <div className="mt-4">
            <a
              href={businessInfo.rabLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-semibold uppercase tracking-wider text-brand-sage-dark underline hover:text-brand-sage transition-colors duration-300"
            >
              Læs mere om RAB-godkendelsen her
            </a>
          </div>
        </div>
      </section>

      {/* Image Gallery Section */}
      <section className="mx-auto max-w-7xl px-6 py-16 md:py-24">
        <GallerySection />
      </section>

      {/* Call to Action Section */}
      <section className="bg-brand-cream/40 py-16 md:py-20 border-t border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl">
            Klar til at opleve Bowen Terapi?
          </h2>
          <p className="mt-4 text-sm text-brand-neutral-dark/80 max-w-xl mx-auto">
            Tag det første skridt mod en mere afspændt krop og et roligere sind. Jeg glæder mig til at tage imod dig i klinikken.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-full bg-brand-sage px-8 py-3.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark hover:shadow-md"
            >
              Bestil en tid nu
            </Link>
            <a
              href={`tel:+45${businessInfo.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center justify-center rounded-full border border-brand-sage-medium/30 bg-white/50 px-8 py-3.5 text-sm font-medium text-brand-neutral-dark transition-all duration-300 hover:bg-white/80"
            >
              Ring på <span className="whitespace-nowrap ml-1">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
